/**
 * VowsEra - Auth Service
 * Pure API calls — no side effects. AuthContext handles token storage.
 */

import api from './api';

const authService = {
  // Browser-safe public config (reCAPTCHA site key etc.). Cached on the
  // module so the Register/Login pages don't refetch on every mount.
  getPublicConfig: (() => {
    let cache = null;
    return async () => {
      if (cache) return cache;
      try {
        const r = await api.get('/public-config');
        cache = r.data?.data || {};
      } catch { cache = {}; }
      return cache;
    };
  })(),

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data; // { success, data: { user, accessToken, refreshToken } }
  },

  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data; // { success, data: { user, accessToken, refreshToken } }
  },

  logout: async () => {
    try { await api.post('/auth/logout'); } catch { /* always succeed client-side */ }
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  changePassword: async (data) => {
    const response = await api.put('/auth/change-password', data);
    return response.data;
  },

  // Two-factor authentication
  toggleTwoFactor: async (enabled, currentPassword) => {
    const response = await api.put('/auth/2fa', { enabled, currentPassword });
    return response.data;
  },

  verify2faLogin: async (email, otp) => {
    const response = await api.post('/auth/verify-2fa-login', { email, otp });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, password, email, otp) => {
    const body = {};
    if (token) { body.token = token; body.password = password; }
    else { body.email = email; body.otp = otp; body.password = password; }
    const response = await api.post('/auth/reset-password', body);
    return response.data;
  },

  resendOtp: async (email, type = 'verify') => {
    const response = await api.post('/auth/resend-otp', { email, type });
    return response.data;
  },

  verifyEmail: async (email, otp) => {
    const response = await api.post('/auth/verify-email', { email, otp });
    return response.data;
  },

  resendVerification: async () => {
    const response = await api.post('/auth/resend-verification');
    return response.data;
  },

  isAuthenticated: () => !!localStorage.getItem('accessToken') // sync check only
};

export default authService;
